import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import chatbotService from '../services/ChatbotService';

/**
 * Single source of truth for "what may this person see?".
 *
 * `/private/features/access` returns both gates in one call:
 *   - `features`    → what the client's PLAN includes
 *   - `permissions` → the person's permission-group permissions ALREADY ∩ plan
 *
 * The backend is the security boundary; this only drives what the UI shows.
 */
const AccessContext = createContext(null);

const normalizeList = (value) => (Array.isArray(value) ? value : [])
    .map((v) => String(v ?? '').trim())
    .filter(Boolean);

export const AccessProvider = ({ group, loginAs, children }) => {
    const currentGroup = String(group ?? '');
    // Admins, customers, and users (members) all resolve access from the backend.
    const shouldLoad = ['admin', 'customer', 'member'].includes(currentGroup) || Boolean(loginAs);

    const [loading, setLoading] = useState(shouldLoad);
    const [error, setError] = useState(false);
    const [features, setFeatures] = useState(() => new Set());
    const [permissions, setPermissions] = useState(() => new Set());
    const [planUpgradeable, setPlanUpgradeable] = useState(false);
    const [planName, setPlanName] = useState('');
    const [role, setRole] = useState('');
    const [reloadKey, setReloadKey] = useState(0);

    const reload = useCallback(() => setReloadKey((k) => k + 1), []);

    useEffect(() => {
        if (!shouldLoad) {
            setFeatures(new Set()); setPermissions(new Set());
            setPlanUpgradeable(false); setPlanName(''); setRole('');
            setLoading(false); setError(false);
            return undefined;
        }
        let cancelled = false;
        (async () => {
            setLoading(true); setError(false);
            try {
                const access = await chatbotService.getFeatureAccess(loginAs);
                if (cancelled) return;
                setFeatures(new Set(normalizeList(access?.features)));
                setPermissions(new Set(normalizeList(access?.permissions)));
                setPlanUpgradeable(Boolean(access?.upgradeable));
                setPlanName(String(access?.planName || access?.planId || '').trim());
                setRole(String(access?.role || '').trim());
            } catch (e) {
                console.error('Failed to load access', e);
                if (cancelled) return;
                setFeatures(new Set()); setPermissions(new Set());
                setPlanUpgradeable(false); setPlanName(''); setRole(''); setError(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [shouldLoad, loginAs, currentGroup, reloadKey]);

    const value = useMemo(() => {
        const ready = shouldLoad && !loading && !error;
        const hasFeature = (key) => {
            if (!ready) return false;
            const w = String(key ?? '').trim().toLowerCase();
            return !!w && Array.from(features).some((f) => String(f).toLowerCase() === w);
        };
        const can = (...wanted) => {
            if (!ready) return false;
            return wanted.flat().some((p) => permissions.has(String(p ?? '').trim()));
        };
        return { loading, error, ready, role, features, permissions, planUpgradeable, planName, hasFeature, can, reload };
    }, [error, features, loading, permissions, planName, planUpgradeable, reload, role, shouldLoad]);

    return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
};

const EMPTY_ACCESS = {
    loading: false, error: false, ready: false, role: '',
    features: new Set(), permissions: new Set(), planUpgradeable: false, planName: '',
    hasFeature: () => false, can: () => false, reload: () => {},
};

export const useAccess = () => useContext(AccessContext) || EMPTY_ACCESS;

export default AccessProvider;
